import { GiCheckMark } from 'react-icons/gi'
import { FaExclamationTriangle } from 'react-icons/fa'
import { HiSelector } from 'react-icons/hi'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { AiFillCloseCircle } from 'react-icons/ai'

const SelectOptionAnim = {
  hidden: { opacity: 0, scaleY: 0 },
  visible: { opacity: 1, scaleY: 1 },
}

let multiSelectTime

const FieldMultiSelect = ({
  fieldProps, fieldMeta, label, edit, options, fieldHelper,
  position = 'bottom', loading = false, dataLoaded = false,
  onChange, disabled = false, injectClass = ""
}) => {
  const input = useRef(null)
  const wrapRef = useRef(null)
  const dropdownRef = useRef(null)

  const [showError, setShowError] = useState(false)
  const [filter, setFilter] = useState('')
  const [focussed, setFocussed] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState({})
  const { t } = useTranslation()

  const toggleFocus = () => setFocussed(prev => !prev)
  const makeFocus = () => input.current?.focus()

  const handleBlur = () => {
    multiSelectTime = setTimeout(() => {
      if (focussed) toggleFocus()
    }, 200)
  }

  const updateDropdownPosition = () => {
    const wrap = wrapRef.current
    if (!wrap) return
    const rect = wrap.getBoundingClientRect()

    const spaceBelow = window.innerHeight - rect.bottom
    const dropdownHeight = 270
    const positionStyles = {
      position: 'absolute',
      width: `${rect.width}px`,
      left: `${rect.left + window.scrollX}px`,
    }

    if (spaceBelow < dropdownHeight) {
      // Show above
      positionStyles.top = `${rect.top + window.scrollY - dropdownHeight}px`
    } else {
      // Show below
      positionStyles.top = `${rect.bottom + window.scrollY}px`
    }

    setDropdownStyle(positionStyles)
  }

  useEffect(() => {
    if (focussed) updateDropdownPosition()

    const handleScrollResize = () => {
      if (focussed) updateDropdownPosition()
    }

    window.addEventListener('scroll', handleScrollResize)
    window.addEventListener('resize', handleScrollResize)

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !wrapRef.current.contains(event.target)
      ) {
        setFocussed(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      window.removeEventListener('scroll', handleScrollResize)
      window.removeEventListener('resize', handleScrollResize)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [focussed])

  const setValue = (ev, value) => {
    ev.stopPropagation()
    clearTimeout(multiSelectTime)

    let newValue = [...fieldProps.value]
    if (newValue.includes(value)) {
      newValue = newValue.filter(c => c !== value)
    } else {
      newValue.push(value)
    }

    if (value === 9) {
      newValue = [9]
    } else if (value !== 9 && newValue.includes(9)) {
      newValue = newValue.filter(c => c !== 9).concat(value)
    }

    fieldHelper.setValue(newValue)
    if (typeof onChange === 'function') onChange(newValue)
  }

  const removeItem = (ev, val) => {
    ev.stopPropagation()
    fieldHelper.setValue(fieldProps.value.filter(c => c !== val))
  }

  const filteredOptions = useMemo(() => {
    return options.filter(c => (
      !filter || c.text.toLowerCase().includes(filter.toLowerCase().trim())
    ))
  }, [filter, options])

  const displayText = useMemo(() => {
    if (!fieldProps.value) return false
    const selected = fieldProps.value
      .map(c => options.find(e => e.value.toString() === c.toString()))
      .filter(Boolean)

    if (selected.length > 0) {
      return (
        <div className={`flex items-center flex-wrap ${focussed ? 'justify-end' : ''}`}>
          {selected.map((c)=>{
            return <span className="text-[10px] transition-all duration-200 bg-bluegray-400 text-white dark:bg-gray-500 px-2 py-0.5 rounded-xl mx-0.5 my-0.5 whitespace-nowrap">
            {c.text}
          </span>
          })}
        </div>
      )
    }
    return false
  }, [fieldProps.value, options, focussed])

  return (
    <motion.div className="relative mt-2 mb-3 p-5">
      {/* Label */}
      <motion.div
        className={`absolute -top-3 left-2 px-5 drop-shadow-sm rounded text-tPColor ${
          disabled ? "bg-[lightGrey] ring-1 ring-[grey]" : "bg-pColor"
        }`}
      >
        {label}
      </motion.div>

      {/* Input */}
      <motion.div
        className={`relative ring-1 ${injectClass} drop-shadow-lg ring-[lightgrey] rounded min-h-11 max-h-auto px-2 py-2 w-full flex flex-col ${
          disabled ? "bg-[lightGrey]" : "bg-transparent"
        }`}
      >
        <div
          ref={wrapRef}
          className="group relative flex flex-col px-2 pt-1 rounded-lg cursor-pointer transition-all duration-100"
          onClick={makeFocus}
        >
          <div className="relative w-full">
            {fieldProps.value.length > 0 && dataLoaded && (
              <div className={`pb-2 min-h-8 max-h-auto ${fieldProps.value.length ? "" : "text-gray-500 line-through"}`}>
                {displayText}
              </div>
            )}

            {edit && dataLoaded && (
              <>
                <input
                  ref={input}
                  className="pl-3 pr-10 py-2 bg-transparent w-full outline-none border-none text-sm"
                  type="text"
                  value={filter}
                  autoComplete="off"
                  onFocus={toggleFocus}
                  onBlur={handleBlur}
                  onChange={(ev) => setFilter(ev.target.value)}
                />
                <span className="absolute right-2 top-2 text-xl flex z-10">
                  {fieldMeta.error && fieldMeta.touched && (
                    <FaExclamationTriangle
                      className="text-red-500 cursor-pointer"
                      onMouseEnter={() => setShowError(true)}
                      onMouseLeave={() => setShowError(false)}
                    />
                  )}
                  <HiSelector className="text-gray-400 ml-2" />
                </span>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Dropdown */}
      {createPortal(
        <AnimatePresence>
          {focussed && (
            <motion.div
              ref={dropdownRef}
              variants={SelectOptionAnim}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="z-[9999] bg-white dark:bg-mdark ring-1 ring-bluegray-300 dark:ring-ldark p-2 rounded shadow-md max-h-[270px] overflow-y-auto"
              style={dropdownStyle}
            >
              {filteredOptions.map((c, i) => (
                <div
                  key={i}
                  onClick={(ev) => {
                    setFilter("")
                    setValue(ev, c.value)
                  }}
                  className="py-2 px-3 hover:bg-primeColor hover:text-white rounded-lg text-sm flex justify-between items-center cursor-pointer"
                >
                  {c.text}
                  {fieldProps.value.includes(c.value) && (
                    <GiCheckMark className="text-green-500 w-4" />
                  )}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </motion.div>
  )
}

export default FieldMultiSelect